import { useEffect, useState } from "react";
import { Check, LogOut } from "lucide-react";
import { api, formatApiError } from "../lib/api";

// Official Microsoft 4-square mark — red / green / blue / yellow
const MicrosoftLogo = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 23 23"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
  </svg>
);

export const MicrosoftConnect = () => {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/microsoft/status");
      setConnected(Boolean(data?.connected));
      setEmail(data?.email || null);
    } catch (e) {
      setConnected(false);
      setEmail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onConnect = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/microsoft/auth-url");
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Could not retrieve Microsoft authorization URL.");
        setBusy(false);
      }
    } catch (e) {
      setError(formatApiError(e));
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete("/microsoft/disconnect");
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="microsoft-connect"
      className="relative bg-brand-deep/40 backdrop-blur-xl border border-brand-mint/15 rounded-2xl p-5 md:p-6"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 shrink-0 rounded-lg bg-brand-deeper/80 border border-brand-mint/15 flex items-center justify-center">
            <MicrosoftLogo className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-brand-mint tracking-tight">
              Microsoft 365
            </h3>
            <p
              data-testid="microsoft-connect-subtitle"
              className="text-xs text-brand-mint/60 truncate"
            >
              {loading
                ? "Checking connection…"
                : connected
                ? "Outlook, Calendar, and OneDrive available in chat."
                : "Connect to let agents read mail, schedule events, and work with your files."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {loading ? (
            <span className="text-xs text-brand-mint/50">…</span>
          ) : connected ? (
            <>
              <span
                data-testid="microsoft-connected-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-xs font-semibold"
              >
                <Check className="h-3 w-3" />
                Microsoft 365 Connected
              </span>
              {email && (
                <span
                  data-testid="microsoft-connected-email"
                  className="hidden sm:inline text-xs text-brand-mint/80 truncate max-w-[220px]"
                >
                  {email}
                </span>
              )}
              <button
                onClick={onDisconnect}
                disabled={busy}
                data-testid="microsoft-disconnect-btn"
                className="inline-flex items-center gap-1.5 text-xs text-brand-mint/60 hover:text-red-200 transition-colors disabled:opacity-50"
              >
                <LogOut className="h-3.5 w-3.5" />
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={onConnect}
              disabled={busy}
              data-testid="microsoft-connect-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-slate-100 text-slate-900 text-sm font-semibold shadow-sm border border-slate-200 transition-colors disabled:opacity-50"
            >
              <MicrosoftLogo className="h-4 w-4" />
              {busy ? "Opening Microsoft…" : "Connect Microsoft 365"}
            </button>
          )}
        </div>
      </div>

      {email && connected && (
        <p
          data-testid="microsoft-connected-email-mobile"
          className="sm:hidden mt-3 text-xs text-brand-mint/70"
        >
          Signed in as {email}
        </p>
      )}

      {error && (
        <div
          data-testid="microsoft-connect-error"
          className="mt-3 text-xs text-red-200 bg-red-500/15 border border-red-400/30 rounded-lg px-3 py-2"
        >
          {error}
        </div>
      )}
    </section>
  );
};

export default MicrosoftConnect;
